import {
  LEVEL_KIND,
  ZONE_KIND,
  ZONE_TOWARDS,
} from "./constants";
import {
  POSITION_DIRECTION,
  POSITION_TONE,
} from "../engine/overlays/positions/positionConstants";

const LEVEL_TONE = {
  [LEVEL_KIND.TAKE_PROFIT]: POSITION_TONE.PROFIT,
  [LEVEL_KIND.STOP_LOSS]: POSITION_TONE.LOSS,
  [LEVEL_KIND.LIQUIDATION]: POSITION_TONE.LOSS,
};

const ZONE_TONE = {
  [ZONE_KIND.PROFIT]: POSITION_TONE.PROFIT,
  [ZONE_KIND.LOSS]: POSITION_TONE.LOSS,
};

const ZONE_DIRECTION = {
  [ZONE_TOWARDS.UP]: POSITION_DIRECTION.UP,
  [ZONE_TOWARDS.DOWN]: POSITION_DIRECTION.DOWN,
};

// "18.018", "10.8108": up to 4 decimals without trailing zeros (moon.com)
const PERCENT_MAX_DECIMALS = 4;
const CENTS_PER_DOLLAR = 100;
const PNL_FORMAT = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
const NO_PNL_TEXT = "—";

const formatPercent = (percent) =>
  String(Number(Math.abs(percent).toFixed(PERCENT_MAX_DECIMALS)));

// "Take Profit - 18.018%"; liquidation costs the whole wager, so no percent (E12)
const formatLevelText = (label, { kind, percent }) =>
  kind === LEVEL_KIND.LIQUIDATION || !Number.isFinite(percent)
    ? label
    : `${label} - ${formatPercent(percent)}%`;

// "$0.01" / "-$0.06" as on moon.com; whole cents, so the sign matches the text
const roundPnl = (pnl) =>
  pnl == null ? null : Math.round(pnl * CENTS_PER_DOLLAR) / CENTS_PER_DOLLAR || 0;

const formatPnl = (pnl) => {
  if (pnl == null) return NO_PNL_TEXT;

  const amount = Math.abs(pnl).toLocaleString("en-US", PNL_FORMAT);

  return pnl < 0 ? `-$${amount}` : `$${amount}`;
};

/**
 * Positions in the shape the chart engine draws (positionsState.ChartPosition):
 * the texts are made here, the engine only paints them.
 *
 * @param {import("./buildPositionLines").PositionLinesModel[]} models
 * @param {{ entry: string, takeProfit: string, stopLoss: string, liquidation: string }} labels
 * @returns {object[]}
 */
export const toChartPositions = (models, labels) =>
  models.map(({ id, isLong, leverage, entry, levels, zones }) => {
    const pnl = roundPnl(entry.pnl);

    return {
      id,
      isLong,
      entry: { price: entry.price, time: entry.time, label: labels.entry },
      card: {
        leverageText: leverage ? `${leverage}x` : "",
        pnl,
        pnlText: formatPnl(pnl),
      },
      levels: levels.map((level) => ({
        tone: LEVEL_TONE[level.kind],
        price: level.price,
        text: formatLevelText(labels[level.kind], level),
      })),
      zones: zones.map(({ kind, price, towards }) => ({
        tone: ZONE_TONE[kind],
        price,
        direction: ZONE_DIRECTION[towards],
      })),
    };
  });
